import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { colors, spacing, borderRadius, Alert } from '@tipsy/design-system';
import type { DashboardStackParamList } from '../navigation/MainNavigator';
import { tipsApi, payoutsApi, type TipStats, type Balance } from '../api';

type NavigationProp = NativeStackNavigationProp<DashboardStackParamList, 'Home'>;

const HomeScreen = () => {
  const navigation = useNavigation<NavigationProp>();
  const [stats, setStats] = useState<TipStats | null>(null);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPayoutLoading, setIsPayoutLoading] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setIsRefreshing(true);
      }
      const [statsData, balanceData] = await Promise.all([
        tipsApi.getStats(),
        payoutsApi.getBalance().catch(() => null),
      ]);
      setStats(statsData);
      setBalance(balanceData);
    } catch {
      setStats({ totalEarnings: 0, todayEarnings: 0, totalTips: 0, todayTips: 0 });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const formatCurrency = (cents: number, currency = 'gbp'): string => {
    const symbol = currency === 'gbp' ? '£' : currency === 'eur' ? '€' : '$';
    return `${symbol}${(cents / 100).toFixed(2)}`;
  };

  const handleInstantPayout = useCallback(async () => {
    if (!balance || balance.instantAvailable <= 0) return;

    const amount = balance.instantAvailable;
    const fee = Math.max(
      Math.round(amount * (balance.instantPayoutFeePercent / 100)),
      balance.instantPayoutMinFee,
    );
    const netAmount = amount - fee;

    Alert.alert(
      'Instant Payout',
      `Withdraw ${formatCurrency(amount, balance.currency)} instantly?\n\nFee: ${formatCurrency(fee, balance.currency)} (${balance.instantPayoutFeePercent}%)\nYou receive: ${formatCurrency(netAmount, balance.currency)}\n\nFunds arrive within minutes.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Withdraw',
          onPress: async () => {
            setIsPayoutLoading(true);
            try {
              await payoutsApi.createInstantPayout(amount);
              Alert.alert('Success', 'Your instant payout is on its way!');
              fetchData();
            } catch (error: unknown) {
              const message =
                (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
                'Unable to process instant payout';
              Alert.alert('Payout Failed', message);
            } finally {
              setIsPayoutLoading(false);
            }
          },
        },
      ],
    );
  }, [balance, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const handleRefresh = () => {
    fetchData(true);
  };

  const formatAmount = (cents: number): string => {
    return (cents / 100).toFixed(2);
  };

  const handleGetQR = () => {
    navigation.navigate('QRCode');
  };

  if (isLoading && !stats) {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.interactive.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.interactive.primary}
          />
        }
      >
        <View style={styles.content}>
          <Image
            source={require('../assets/logo-black.png')}
            style={styles.logo}
            resizeMode="contain"
          />

          <View style={styles.earningsContainer}>
            <Text style={styles.earningsLabel}>Total Earnings</Text>
            <Text style={styles.earningsAmount}>${formatAmount(stats?.totalEarnings ?? 0)}</Text>
          </View>

          <View style={styles.statsRow}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>${formatAmount(stats?.todayEarnings ?? 0)}</Text>
              <Text style={styles.statLabel}>Today</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{stats?.totalTips ?? 0}</Text>
              <Text style={styles.statLabel}>Total Tips</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{stats?.todayTips ?? 0}</Text>
              <Text style={styles.statLabel}>Today Tips</Text>
            </View>
          </View>

          {balance && balance.instantPayoutsAvailable && balance.instantAvailable > 0 && (
            <TouchableOpacity
              style={styles.instantPayoutButton}
              onPress={handleInstantPayout}
              disabled={isPayoutLoading}
            >
              {isPayoutLoading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.instantPayoutText}>Withdraw Funds</Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity style={styles.qrButton} onPress={handleGetQR}>
          <Text style={styles.qrButtonText}>Get QR</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingBottom: spacing.xl,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.base,
  },
  logo: {
    width: 200,
    height: 200,
    alignSelf: 'center',
    marginBottom: spacing.xl,
  },
  earningsContainer: {
    alignItems: 'center',
    marginBottom: spacing['3xl'],
  },
  earningsLabel: {
    fontSize: 18,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
  },
  earningsAmount: {
    fontSize: 48,
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  statsRow: {
    flexDirection: 'row',
    backgroundColor: colors.surface.primary,
    borderRadius: borderRadius.lg,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.base,
    width: '100%',
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  statLabel: {
    fontSize: 13,
    color: colors.text.tertiary,
  },
  statDivider: {
    width: 1,
    backgroundColor: colors.border.light,
    marginVertical: spacing.xs,
  },
  instantPayoutButton: {
    backgroundColor: '#22C55E',
    borderRadius: borderRadius.lg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    marginTop: spacing.xl,
    alignItems: 'center',
    width: '100%',
  },
  instantPayoutText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  footer: {
    paddingHorizontal: spacing.base,
    paddingBottom: spacing.base,
  },
  qrButton: {
    backgroundColor: '#000000',
    borderRadius: borderRadius.lg,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  qrButtonText: {
    color: '#FFDD00',
    fontSize: 18,
    fontWeight: '600',
  },
});

export default HomeScreen;
